import { ErrorHandler, Injectable, inject } from '@angular/core';
import {
  Router,
  type NavigationBehaviorOptions,
  type NavigationExtras,
  type UrlTree,
} from '@angular/router';

/**
 * Fire-and-forget navigation that reports failures instead of dropping them.
 *
 * `Router.navigate*()` returns a promise that rejects on a genuine fault — most
 * commonly a lazy-chunk load error after a deploy, but also a throwing guard or
 * resolver. Call sites almost never await it, so without an explicit handler
 * those faults vanish silently (a bare `void` prefix satisfies the linter but
 * attaches no handler). Routing the rejection to the application `ErrorHandler`
 * sends it on to Sentry.
 *
 * Distinct from `NavigationService`, which owns sidebar search/collapse state
 * and has nothing to do with routing.
 */
@Injectable({ providedIn: 'root' })
export class RouteNavigator {
  private readonly router = inject(Router);
  private readonly errorHandler = inject(ErrorHandler);

  /** Navigate to a URL or `UrlTree`, reporting any failure. */
  toUrl(url: string | UrlTree, extras?: NavigationBehaviorOptions): void {
    this.router
      .navigateByUrl(url, extras)
      .catch((error: unknown) => this.errorHandler.handleError(error));
  }

  /** Navigate by command array, reporting any failure. */
  to(commands: readonly unknown[], extras?: NavigationExtras): void {
    this.router
      .navigate([...commands], extras)
      .catch((error: unknown) => this.errorHandler.handleError(error));
  }
}
